import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  Injector,
  Input,
  input,
  OnChanges,
  OnInit,
  Output,
  ElementRef,
  ViewChild,
  signal
} from '@angular/core';
import { ButtonComponent } from '@pc/button';
import { DialogAbstractComponent, DialogSize } from '@pc/dialog-abstract';
import { GradientBtnComponent, PcButtonType } from '@pc/gradient-btn';
import { IconComponent } from '@pc/icon';
import { BreakpointObserverService } from '@pc/shared';
import { ComponentSizes, PcButtonIcon } from '@pc/common';
import { ToggleButtonType } from '@pc/toggle-button';
import { CdkScrollable } from '@angular/cdk/overlay';
import { SeparatorComponent } from '@pc/separator';

const SINGLE_CHAR_WIDTH = 8;

@Component({
  selector: 'pc-error-popup',
  standalone: true,
  imports: [
    CommonModule,
    CdkScrollable,
    DialogAbstractComponent,
    GradientBtnComponent,
    IconComponent,
    ButtonComponent,
    SeparatorComponent
  ],
  templateUrl: './error-popup.component.html',
  styleUrl: './error-popup.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ErrorPopupComponent implements OnInit, AfterViewInit, OnChanges {
  @Input({ required: true }) title!: string;
  @Input({ required: true }) mobileImgPath!: string;
  @Input({ required: true }) desktopImgPath!: string;
  @Input({ required: true }) imgAlt!: string;
  @Input() shouldUpdateButtonsSizeForAnimation = false;
  @Input() description = '';
  @Input() information = '';
  /* dynamic-height class make height of the dialog dynamic */
  @Input() contentClass: string | 'dynamic-height' = 'align-center';
  @Input() buttonText = '';
  @Input() buttonTextSecondary = '';
  @Input() buttonSecondaryInnerHtmlText = '';
  @Output() closeErrorDialog = new EventEmitter<void>();
  @Output() closeWithSubmit = new EventEmitter<void>();
  @Output() closeWithSubmitSecondary = new EventEmitter<void>();
  @Output() alternativeButtonClicked = new EventEmitter<void>();
  @Output() cardButtonsSelected = new EventEmitter<ToggleButtonType[]>();
  @Output() additionalQuestionsSelected = new EventEmitter<ToggleButtonType[]>();
  @ViewChild('scrollableSection', { static: false }) scrollableSectionRef!: ElementRef;
  @ViewChild('projectedContentWrapper', { static: false }) projectedContentWrapperRef!: ElementRef;

  public readonly injector = inject(Injector);
  public readonly breakpoint = inject(BreakpointObserverService);
  public isDesktop = this.breakpoint.getMediumBreakpoint(this.injector);
  public subInformation = input('');
  public transparentButtonText = input('');
  public ariaLabelledBy = input('popupTitle');
  public ariaLabelText = input('');
  public isCloseIcon = input(false);
  public activeSection = computed(() => this.description || this.information || this.subInformation());
  public submitDisabled = input({ value: false });
  public submitSecondaryDisabled = input({ value: false });
  public isPopUpWithoutAnimationOnClose = input(false);
  public isBasicCoversPopUp = input(false);
  public inputMobileSize = input<DialogSize>('auto');
  public closePopUpAriaLabel = input('');
  public submitButtonIcon = input('');
  public secondaryButtonIcon = input('');
  public secondaryButtonAnimation = input(false);
  public isButtonLoader = input(false);
  public isButtonLoaderSecondary = input(false);
  public secondaryButtonType = input<PcButtonType>(PcButtonType.secondary);
  public buttonsContainerFlexDirection = input<'row' | 'column'>('column');
  public isButtonsPartOfScrollingContainer = input(false);
  public btnPadding = input<number | undefined>();
  public secondaryBtnPadding = input<number | undefined>();
  public isSecondaryBtnTextImageReverseOrder = input(false);
  public secondaryBtnMinWidth = input(0);
  public enableFadeOverlay = input(false);
  public shouldDisplayFadeOverlay = signal(false);
  public xicon = PcButtonIcon.POPUP_X_CLOSE;
  public iconSize: ComponentSizes = 'xsmall';

  public size: DialogSize = 'auto';

  public useWindowSizeUpdater = input<boolean>(true);
  public btnMinWidth = 0;

  private timeoutIds: number[] = [];
  private destroyRef = inject(DestroyRef);

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.timeoutIds.forEach((id) => clearTimeout(id));
    });
  }

  public ngOnInit() {
    if (this.shouldUpdateButtonsSizeForAnimation) {
      this.updateButtonsSize();
    }
  }

  public close(): void {
    this.closeErrorDialog.emit();
  }

  public onAlternativeButton(): void {
    this.alternativeButtonClicked.emit();
  }

  public closeSubmit(): void {
    this.closeWithSubmit.emit();
  }

  public closeSubmitSecondary(): void {
    this.closeWithSubmitSecondary.emit();
  }

  public ngAfterViewInit(): void {
    this.setScrollingOnInit();

    if (!this.enableFadeOverlay()) return;
    const scrollable = this.scrollableSectionRef.nativeElement as HTMLElement;
    this.initFadeOverlay(scrollable);
  }

  public ngOnChanges(): void {
    this.setScrolling();
  }

  private setScrolling(): void {
    if (!this.isBasicCoversPopUp()) return;
    this.timeoutIds.push(
      window.setTimeout(() => {
        document.getElementById('scrollToElement')?.scrollIntoView({ behavior: 'smooth' });
      }, 100)
    );
  }

  private setScrollingOnInit(): void {
    if (!this.isBasicCoversPopUp()) return;
    this.timeoutIds.push(
      window.setTimeout(() => {
        document.getElementById('scrollToElementOnInit')?.scrollIntoView({ behavior: 'smooth' });
      }, 100)
    );
  }

  private updateButtonsSize() {
    // addedWidth is the desired (padding + arrow icon) for the button according to the design
    const addedWidth = 80;
    const primaryButtonContentLength = (this.buttonText || '').length;
    const secondaryButtonContentLength = (this.buttonTextSecondary || '').length;

    const longestTextWidth =
      primaryButtonContentLength > secondaryButtonContentLength
        ? primaryButtonContentLength * SINGLE_CHAR_WIDTH
        : secondaryButtonContentLength * SINGLE_CHAR_WIDTH;

    this.btnMinWidth = longestTextWidth + addedWidth;
  }

  private initFadeOverlay(scrollable: HTMLElement): void {
    const overlay = scrollable.closest('.error-popup')?.querySelector('.fade-overlay') as HTMLElement;
    if (!overlay) return;

    const update = () => requestAnimationFrame(() => this.updateFadeOverlayVisibility(scrollable, overlay));

    scrollable.addEventListener('scroll', update);

    const projectedContent = this.projectedContentWrapperRef?.nativeElement;
    if (projectedContent) {
      const projectedResizeObserver = new ResizeObserver(update);
      projectedResizeObserver.observe(projectedContent);
      this.destroyRef.onDestroy(() => projectedResizeObserver.disconnect());
    }

    update();
  }

  private updateFadeOverlayVisibility(scrollable: HTMLElement, overlay: HTMLElement): void {
    this.shouldDisplayFadeOverlay.set(scrollable.scrollHeight > scrollable.clientHeight);
    if (!this.shouldDisplayFadeOverlay()) return;

    const scrollTop = scrollable.scrollTop;
    const scrollHeight = scrollable.scrollHeight;
    const clientHeight = scrollable.clientHeight;

    const distanceToBottom = scrollHeight - clientHeight - scrollTop;

    const fadeDistance = 60;
    const opacity = Math.min(distanceToBottom / fadeDistance, 1);
    overlay.style.opacity = `${opacity}`;
    overlay.style.bottom = '0';
  }
}
